// Iter116.6 — Shared Copy + CSV bulk-export toolbar.
//
// Originally lived inside Performance.js; extracted so the Decision Journal,
// Performance, and Leaderboard tables can all share the SAME UX language
// (green "Copy all (N)" pill + amber "CSV" pill) without duplicating logic.
//
// Pure client-side — no backend changes required. Blob + UTF-8 BOM + temp
// anchor handles the CSV download path; `navigator.clipboard.writeText`
// (with a textarea fallback) handles the multi-line plain-text export.

import React, { useState } from "react";
import { Copy, Check, Download } from "lucide-react";

export function csvEscape(val) {
  if (val === null || val === undefined) return "";
  const s = String(val);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function downloadCsvFile(filename, csvText) {
  // BOM so Excel opens UTF-8 CSV cleanly.
  const blob = new Blob(["\uFEFF", csvText], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export async function writeClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand("copy"); } catch { return false; }
    document.body.removeChild(ta);
    return true;
  }
}

/**
 * TableExportToolbar — drops [Copy all (N)] + [CSV] buttons next to any
 * table.
 *
 * Props:
 *   rows         — array of row data
 *   label        — header line printed at top of clipboard payload
 *   csvFilename  — base filename (date stamp appended)
 *   buildLine    — (row) => string  used for the per-row clipboard bullet
 *   csvColumns   — [{ label, get(row) }, ...] for CSV columns
 *   testidPrefix — `${prefix}-copy-all` + `${prefix}-download-csv`
 */
export function TableExportToolbar({
  rows, label, csvFilename, buildLine, csvColumns, testidPrefix,
}) {
  const [copied, setCopied] = useState(false);
  const disabled = !rows || rows.length === 0;

  const handleCopy = async () => {
    if (disabled) return;
    const ts = new Date().toLocaleString();
    const header = `${label} — ${rows.length} row${rows.length === 1 ? "" : "s"} — ${ts}`;
    const lines = [header, ""].concat(rows.map(r => `• ${buildLine(r)}`));
    await writeClipboard(lines.join("\n"));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const handleCsv = () => {
    if (disabled) return;
    const headerLine = csvColumns.map(c => c.label).map(csvEscape).join(",");
    const rowLines = rows.map((r, i) =>
      csvColumns.map(c => csvEscape(c.get(r, i))).join(",")
    );
    const csv = [headerLine].concat(rowLines).join("\n");
    const today = new Date().toISOString().slice(0, 10);
    downloadCsvFile(`${csvFilename}-${today}.csv`, csv);
  };

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        data-testid={`${testidPrefix}-copy-all`}
        onClick={handleCopy}
        disabled={disabled}
        title={`Copy all ${rows?.length || 0} rows as a multi-line summary`}
        className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-sm border transition-colors ${
          copied
            ? "bg-[#00FF88]/10 text-[#00FF88] border-[#00FF88]/40"
            : "bg-[#0A0A0E] text-[#8A8A93] border-[#26262B] hover:border-[#00FF88]/40 hover:text-[#00FF88] disabled:opacity-30 disabled:cursor-not-allowed"
        }`}
      >
        {copied ? <Check size={11} /> : <Copy size={11} />}
        <span>{copied ? "Copied" : `Copy all${rows?.length > 0 ? ` (${rows.length})` : ""}`}</span>
      </button>
      <button
        type="button"
        data-testid={`${testidPrefix}-download-csv`}
        onClick={handleCsv}
        disabled={disabled}
        title="Download all visible rows as CSV (Excel-friendly)"
        className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-sm border bg-[#0A0A0E] text-[#8A8A93] border-[#26262B] hover:border-[#FFB800]/40 hover:text-[#FFB800] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        <Download size={11} />
        CSV
      </button>
    </div>
  );
}
